import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { projects as projectsApi } from "@/lib/api";
import Nav from "@/components/Nav";
import { ArrowRight, Loader2, Mail, Lock, Phone, User2 } from "lucide-react";

export default function AuthPage() {
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ email: "", phone: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const claimId = params.get("claim");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      if (claimId) {
        try { await projectsApi.claim(claimId); } catch { /* ignore */ }
        toast.success("Project saved to your dashboard");
      }
      nav(claimId ? "/dashboard" : "/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="pt-16 pb-16 px-6 flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="kd-panel p-8 md:p-12 w-full max-w-md">
          <div className="kd-tag mb-4">{mode === "signup" ? "Create account" : "Welcome back"}</div>
          <h1 className="kd-display text-3xl md:text-4xl tracking-tight mb-3">
            {mode === "signup" ? "Save your decks." : "Sign in."}
          </h1>
          <p className="text-zinc-400 text-sm mb-8">
            {mode === "signup"
              ? "Email + phone number to keep every deck in your dashboard."
              : "Access your saved decks, downloads and revisions."}
          </p>
          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <IconField icon={User2} placeholder="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="auth-name" />
            )}
            <IconField icon={Mail} placeholder="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="auth-email" required />
            {mode === "signup" && (
              <IconField icon={Phone} placeholder="Phone (e.g. +15551234567)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="auth-phone" required />
            )}
            <IconField icon={Lock} placeholder="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="auth-password" required />

            <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full rounded-full px-6 py-3.5 bg-[#E50914] hover:bg-[#FF2A2D] text-white font-medium transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)] disabled:opacity-40 flex items-center justify-center gap-2 mt-4">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {mode === "signup" ? "Create account" : "Sign in"} <ArrowRight size={16} />
            </button>
          </form>
          <div className="mt-6 text-sm text-zinc-400 text-center">
            {mode === "signup" ? "Already have an account?" : "New to KlickDeck?"}{" "}
            <button data-testid="auth-toggle" onClick={() => setMode(mode === "signup" ? "login" : "signup")} className="text-white hover:text-[#FF2A2D] transition-colors underline underline-offset-4">
              {mode === "signup" ? "Sign in" : "Create one"}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function IconField({ icon: Icon, placeholder, value, onChange, type = "text", testId, required }) {
  return (
    <label className="block">
      <div className="relative">
        <Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type={type}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
          className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white text-sm outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 placeholder:text-zinc-600"
        />
      </div>
    </label>
  );
}
